import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  FluentProvider,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import {
  Search20Regular,
  Dismiss20Regular,
  ArrowClockwiseDashesColor,
  Add20Regular,
} from '@fluentui/react-icons';
import type { IRowMenuOption } from '../DataGrid/DataGridUI';
import { FilterIcon } from '@fluentui/react-icons-mdl2';
import {
  internalFetch,
  type IMetadata,
  type IPagedCollection,
} from '@moko/utils';
import useFetch from '../hooks/useFetch';
import { useObjectState } from '../hooks/useObjectState';
import PaginationBar from '../DataGrid/PaginationBar';
import { resolveCommandBarAppearance } from '../DataGrid/commandBar';
import CardList from './CardList';
import type { IStatusOption } from '../StatusBadge';
import { buildCardData, resolveMetadataForItem } from './cardUtils';
import type { EmptyStateConfig } from '../Empty/Empty';
import { renderEmptyState } from '../Empty/renderEmptyState';
import {
  DataGridFilters,
  FilterForm,
  buildDefaultFilters,
  serializeFilters,
  type IActiveFilter,
  type IFilterableColumn,
} from '../DataGrid/Filter';

export interface ICardCommandBarItem {
  key: string;
  text: string;
  icon?: React.JSX.Element;
  disabled?: boolean;
  appearance?: 'primary' | 'subtle' | 'outline' | 'secondary' | 'transparent';
  style?: React.CSSProperties;
  onClick?: (context: { refresh: () => void }) => void;
}

export interface PagedCardListRef {
  refresh: () => void;
}

const backendApiUrl = import.meta.env.VITE_BACKEND_API_URL as string;

interface MetadatasResponse {
  metadatas: Record<string, IMetadata>;
}

const fetchMetadatasByEntity = (
  abortController: AbortController,
  entity: string
): Promise<MetadatasResponse> =>
  internalFetch(
    `${backendApiUrl}/p/latestMetadatas?entityTypes=${entity}`,
    'GET',
    abortController
  );

export interface PagedCardListProps {
  entity: string;
  getAction: (
    abortController: AbortController,
    pageSize: number,
    pageNo: number,
    search?: string,
    filters?: string
  ) => Promise<IPagedCollection<Record<string, unknown>>>;
  pageSize?: number;
  toolbarItems?: ICardCommandBarItem[];
  onCardClick?: (id: number) => void;
  onSelectionChange?: (id: number) => void;
  /** Selection mode. When 'multiselect', cards render checkboxes and a
   * 'select all on page' header is shown. Selection persists across page
   * changes and is cleared on search/filter change. */
  selectionMode?: 'single' | 'multiselect';
  /** Called with the full set of selected items (multiselect only). */
  onMultiSelectionChange?: (items: Record<string, unknown>[]) => void;
  onLoading?: () => void;
  onLoaded?: () => void;
  onError?: (error: Error) => void;
  searchable?: boolean;
  fallbackField?: string;
  onRefresh?: () => void;
  filterEnabled?: boolean;
  filterColumns?: IFilterableColumn[];
  /**
   * When provided, tapping a card opens a context menu populated with
   * these options. Independent of selection — consumers opt out of
   * selection by simply not passing `onSelectionChange`. `onCardClick` is
   * ignored when the menu is active.
   */
  rowMenuOptions?: IRowMenuOption<Record<string, unknown>>[];
  statuses?: readonly IStatusOption[];
  /**
   * Inline mode: render a single, fixed page of cards as a horizontal row
   * (wrapping) with no refresh/search/filter chrome and no pagination. Used
   * to surface the same cards "outside" a drawer (e.g. a recent-orders row).
   * `pageSize` controls how many cards are shown.
   */
  horizontal?: boolean;
  /** Optional content rendered at the bottom of each card (e.g. a
   * "Fetch details" link). Receives the card id. */
  renderCardFooter?: (id: number) => React.ReactNode;
  renderCardAction?: (
    id: number,
    item: Record<string, unknown>
  ) => React.ReactNode;
  showFieldLabels?: boolean;
  /**
   * Controlled highlight: when provided, the card with this id renders with
   * the "selected" (primary border) style regardless of internal selection.
   * Used to indicate which card is currently chosen (e.g. seeded for prefill)
   * without enabling the radio/checkbox selection UI.
   */
  selectedId?: number | null;
  hidePagination?: boolean;
  empty?: EmptyStateConfig;
}

interface ApiParams {
  page: number;
  search: string;
  filters: IActiveFilter[];
  refreshCount: number;
}

const PagedCardList = React.forwardRef<PagedCardListRef, PagedCardListProps>(
  (props, ref) => {
    const theme = useActiveTheme();
    const {
      entity,
      getAction,
      pageSize = 10,
      toolbarItems,
      onCardClick,
      onSelectionChange,
      selectionMode,
      onMultiSelectionChange,
      onLoading,
      onLoaded,
      onError,
      onRefresh,
      searchable = true,
      fallbackField = 'code',
      filterEnabled = false,
      filterColumns,
      rowMenuOptions,
      statuses,
      horizontal = false,
      renderCardFooter,
      renderCardAction,
      showFieldLabels = true,
      selectedId: controlledSelectedId,
      hidePagination = false,
      empty,
    } = props;

    // Inline/horizontal mode is a chrome-less single page: no refresh,
    // search, filters, multiselect header or pagination.
    const showChrome = !horizontal;

    const {
      state: params,
      updateProperty,
      bulkUpdate,
    } = useObjectState<ApiParams>({
      initialState: { page: 1, search: '', filters: [], refreshCount: 0 },
    });

    // Seed any default-flagged column the first time it becomes available.
    // Tracked per-key so columns added later (e.g. async metadata that
    // resolves after first render) are still seeded, while keys the user has
    // since removed/edited are never re-seeded.
    const seededKeysRef = useRef<Set<string>>(new Set());
    useEffect(() => {
      if (!filterEnabled) return;
      const fresh = buildDefaultFilters(filterColumns ?? []).filter(
        (f) => !seededKeysRef.current.has(f.column.key)
      );
      if (fresh.length === 0) return;
      fresh.forEach((f) => seededKeysRef.current.add(f.column.key));
      bulkUpdate({ filters: [...params.filters, ...fresh] });
    }, [filterEnabled, filterColumns, params.filters, bulkUpdate]);

    const [searchValue, setSearchValue] = useState('');
    const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
    const [addFilterOpen, setAddFilterOpen] = useState(false);

    // Persistent multi-select state — survives page changes, cleared when
    // search/filter change.
    const isMultiSelect = selectionMode === 'multiselect';
    const [selectedItemMap, setSelectedItemMap] = useState<
      Map<number, Record<string, unknown>>
    >(new Map());

    // Row menu state (when rowMenuOptions is provided) — anchored to the
    // click coordinates via a Fluent UI virtual positioning target so the
    // menu opens at the cursor / tap point.
    const hasRowMenu = !!rowMenuOptions && rowMenuOptions.length > 0;
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuItem, setMenuItem] = useState<Record<string, unknown> | null>(
      null
    );
    const [menuVirtualTarget, setMenuVirtualTarget] = useState<{
      getBoundingClientRect: () => DOMRect;
    } | null>(null);

    const serializedFilters = useMemo(
      () => (params.filters.length > 0 ? serializeFilters(params.filters) : ''),
      [params.filters]
    );

    // Clear multi-select when search/filter change (stale ids would leak
    // into bulk actions).
    useEffect(() => {
      if (!isMultiSelect) return;
      setSelectedItemMap(new Map());
      onMultiSelectionChange?.([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.search, serializedFilters, isMultiSelect]);

    const { data: metadatasResponse } = useFetch<MetadatasResponse>({
      queryKey: [entity],
      queryFn: (abortController) =>
        fetchMetadatasByEntity(abortController, entity),
    });

    const metadataMap = metadatasResponse?.metadatas ?? {};

    const { data, loading } = useFetch<
      IPagedCollection<Record<string, unknown>>
    >({
      queryKey: [
        params.page,
        params.search,
        serializedFilters,
        params.refreshCount,
      ],
      queryFn: (abortController) =>
        getAction(
          abortController,
          pageSize,
          params.page,
          params.search,
          serializedFilters || undefined
        ),
      onLoading,
      onLoaded,
      onError,
    });

    const refresh = useCallback(() => {
      updateProperty('refreshCount', params.refreshCount + 1);
      onRefresh?.();
    }, [updateProperty, params.refreshCount, onRefresh]);

    useImperativeHandle(ref, () => ({ refresh }));

    const context = useMemo(() => ({ refresh }), [refresh]);

    const cards = useMemo(() => {
      if (!data?.items) return [];
      return data.items.map((item) => {
        const metadata = resolveMetadataForItem(metadataMap, entity, item);
        return buildCardData(metadata, item, fallbackField);
      });
    }, [metadataMap, entity, data?.items, fallbackField]);

    const handleSearch = useCallback(
      () => bulkUpdate({ page: 1, search: searchValue }),
      [bulkUpdate, searchValue]
    );

    const handleClearSearch = useCallback(() => {
      setSearchValue('');
      bulkUpdate({ page: 1, search: '' });
    }, [bulkUpdate]);

    const handleFilterApply = useCallback(
      (filter: IActiveFilter) => {
        bulkUpdate({ page: 1, filters: [...params.filters, filter] });
        setAddFilterOpen(false);
      },
      [params.filters, bulkUpdate]
    );

    const handleFilterUpdate = useCallback(
      (index: number, filter: IActiveFilter) => {
        const newFilters = [...params.filters];
        newFilters[index] = filter;
        bulkUpdate({ page: 1, filters: newFilters });
      },
      [params.filters, bulkUpdate]
    );

    const handleFilterRemove = useCallback(
      (index: number) => {
        const newFilters = params.filters.filter((_, i) => i !== index);
        bulkUpdate({ page: 1, filters: newFilters });
      },
      [params.filters, bulkUpdate]
    );

    const handleCardSelect = useCallback(
      (id: number) => {
        if (isMultiSelect) {
          const item = data?.items.find(
            (it: Record<string, unknown>) => (it.id as number) === id
          );
          if (!item) return;
          setSelectedItemMap((prev) => {
            const next = new Map(prev);
            if (next.has(id)) next.delete(id);
            else next.set(id, item);
            onMultiSelectionChange?.(Array.from(next.values()));
            return next;
          });
          return;
        }
        setSelectedCardId((prev) => (prev === id ? null : id));
        onSelectionChange?.(id);
      },
      [isMultiSelect, data?.items, onMultiSelectionChange, onSelectionChange]
    );

    const pageCount = data?.pageCount ?? 0;
    const isLastPage = data?.isLastPage ?? true;
    const hasFilterableColumns =
      filterEnabled && (filterColumns ?? []).length > 0;
    // `rowMenuOptions` is independent from selection — consumers opt out of
    // selection by simply not passing `onSelectionChange`.
    const selectable = isMultiSelect || !!onSelectionChange;

    // Visible-page subset of persisted ids (drives the "select all on page"
    // header checkbox state).
    const pageIds = useMemo<number[]>(
      () =>
        (data?.items ?? []).map(
          (it: Record<string, unknown>) => it.id as number
        ),
      [data?.items]
    );
    const pageSelectedCount = useMemo(
      () => pageIds.filter((id) => selectedItemMap.has(id)).length,
      [pageIds, selectedItemMap]
    );
    const allOnPageSelected =
      pageIds.length > 0 && pageSelectedCount === pageIds.length;
    const someOnPageSelected =
      pageSelectedCount > 0 && pageSelectedCount < pageIds.length;

    const handleToggleSelectAllOnPage = useCallback(() => {
      if (!data?.items) return;
      setSelectedItemMap((prev) => {
        const next = new Map(prev);
        if (allOnPageSelected) {
          pageIds.forEach((id) => next.delete(id));
        } else {
          data.items.forEach((it: Record<string, unknown>) => {
            next.set(it.id as number, it);
          });
        }
        onMultiSelectionChange?.(Array.from(next.values()));
        return next;
      });
    }, [allOnPageSelected, data?.items, pageIds, onMultiSelectionChange]);

    const selectedIdsSet = useMemo<Set<number>>(
      () => new Set(selectedItemMap.keys()),
      [selectedItemMap]
    );

    const handleCardMenuTrigger = useCallback(
      (id: number, e?: React.MouseEvent<HTMLElement>) => {
        if (!hasRowMenu || !data?.items) return;
        const item = data.items.find(
          (it: Record<string, unknown>) => it.id === id
        );
        if (!item) return;
        setMenuItem(item);
        if (e) {
          const x = e.clientX;
          const y = e.clientY;
          setMenuVirtualTarget({
            getBoundingClientRect: () =>
              ({
                x,
                y,
                top: y,
                left: x,
                right: x,
                bottom: y,
                width: 0,
                height: 0,
                toJSON: () => ({}),
              } as DOMRect),
          });
        }
        setMenuOpen(true);
      },
      [hasRowMenu, data?.items]
    );

    const handleMenuOptionClick = useCallback(
      (option: IRowMenuOption<Record<string, unknown>>) => {
        if (!menuItem) return;
        option.onClick(menuItem, { refresh });
        setMenuOpen(false);
      },
      [menuItem, refresh]
    );

    const effectiveCardClick = hasRowMenu ? handleCardMenuTrigger : onCardClick;

    const actionItems = toolbarItems?.filter((i) => i.key !== 'refresh') ?? [];
    const hasActions = actionItems.length > 0;

    const availableFilterColumns = hasFilterableColumns
      ? filterColumns!.filter(
          (c) => !params.filters.some((f) => f.column.key === c.key)
        )
      : [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Row 0: Refresh — bordered, right-aligned */}
        {showChrome && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '4px 0',
            }}
          >
            <Button
              appearance="outline"
              icon={<ArrowClockwiseDashesColor />}
              onClick={refresh}
              size="small"
              style={{ paddingTop: 6, paddingBottom: 6 }}
            >
              Refresh
            </Button>
          </div>
        )}

        {/* Row 1: Search + Filter icon-only button */}
        {showChrome && searchable && (
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 16,
              padding: '4px 0',
            }}
          >
            <Input
              placeholder="Search"
              style={{ flex: 1 }}
              value={searchValue}
              onChange={(_, d) => setSearchValue(d.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              contentBefore={<Search20Regular />}
              contentAfter={
                searchValue ? (
                  <Dismiss20Regular
                    style={{ cursor: 'pointer' }}
                    onClick={handleClearSearch}
                  />
                ) : undefined
              }
            />
            {hasFilterableColumns && (
              <FluentProvider
                theme={theme}
                style={{ background: 'transparent' }}
              >
                <Popover
                  open={addFilterOpen}
                  onOpenChange={(_, data) => setAddFilterOpen(data.open)}
                  positioning="below"
                >
                  <PopoverTrigger disableButtonEnhancement>
                    <Button
                      appearance="primary"
                      icon={
                        params.filters.length > 0 ? (
                          <Add20Regular />
                        ) : (
                          <FilterIcon style={{ fontSize: 14 }} />
                        )
                      }
                      aria-label={
                        params.filters.length > 0
                          ? 'Add more filters'
                          : 'Add filter'
                      }
                      style={{
                        height: '100%',
                        aspectRatio: '1',
                        minWidth: 0,
                        padding: 0,
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverSurface>
                    <FilterForm
                      columns={availableFilterColumns}
                      onSubmit={handleFilterApply}
                      onCancel={() => setAddFilterOpen(false)}
                    />
                  </PopoverSurface>
                </Popover>
              </FluentProvider>
            )}
          </div>
        )}

        {/* Active filter tags */}
        {showChrome && hasFilterableColumns && params.filters.length > 0 && (
          <FluentProvider
            theme={theme}
            style={{ background: 'transparent', padding: '4px 0' }}
          >
            <DataGridFilters
              columns={filterColumns!}
              filters={params.filters}
              onApply={handleFilterApply}
              onUpdate={handleFilterUpdate}
              onRemove={handleFilterRemove}
              compact
            />
          </FluentProvider>
        )}

        {/* Row 2: Action buttons */}
        {hasActions && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              padding: '4px 0',
            }}
          >
            {actionItems.map((item) => (
              <Button
                key={item.key}
                appearance={resolveCommandBarAppearance(
                  item.key,
                  item.appearance
                )}
                icon={item.icon}
                disabled={item.disabled}
                onClick={() => item.onClick?.(context)}
                size="small"
                style={{
                  paddingTop: 4,
                  paddingBottom: 4,
                  ...item.style,
                }}
              >
                {item.text}
              </Button>
            ))}
          </div>
        )}

        {/* Cards */}
        <div style={{ marginTop: 8 }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: horizontal ? 16 : 40,
              }}
            >
              <Spinner size={horizontal ? 'tiny' : 'medium'} />
            </div>
          ) : cards.length === 0 ? (
            renderEmptyState(empty)
          ) : (
            <>
              {showChrome && isMultiSelect && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 4px 8px',
                  }}
                >
                  <Checkbox
                    checked={
                      allOnPageSelected
                        ? true
                        : someOnPageSelected
                        ? 'mixed'
                        : false
                    }
                    onChange={handleToggleSelectAllOnPage}
                    label={
                      allOnPageSelected ? 'Deselect page' : 'Select all on page'
                    }
                  />
                </div>
              )}
              <CardList
                cards={cards}
                onCardClick={effectiveCardClick}
                selectable={selectable}
                selectedId={
                  isMultiSelect
                    ? null
                    : controlledSelectedId !== undefined
                    ? controlledSelectedId
                    : selectedCardId
                }
                selectedIds={isMultiSelect ? selectedIdsSet : undefined}
                onSelect={handleCardSelect}
                statuses={statuses}
                horizontal={horizontal}
                renderCardFooter={renderCardFooter}
                renderCardAction={
                  renderCardAction
                    ? (id) => {
                        const item = data?.items.find(
                          (it) => (it.id as number) === id
                        );
                        return item ? renderCardAction(id, item) : null;
                      }
                    : undefined
                }
                showFieldLabels={showFieldLabels}
              />
            </>
          )}
        </div>

        {/* Pagination */}
        {showChrome && !hidePagination && cards.length > 0 && (
          <PaginationBar
            page={params.page}
            pageCount={pageCount}
            isLastPage={isLastPage}
            onFirst={() => updateProperty('page', 1)}
            onPrev={() => updateProperty('page', Math.max(1, params.page - 1))}
            onNext={() => updateProperty('page', params.page + 1)}
            onLast={() => updateProperty('page', pageCount)}
          />
        )}

        {/*
          Row menu — wrapped in a tight FluentProvider so the v9 Menu
          renders with the correct theme without leaking styles to the
          surrounding (non-v9) page chrome.
        */}
        {hasRowMenu && (
          <FluentProvider theme={theme} style={{ background: 'transparent' }}>
            <Menu
              open={menuOpen}
              onOpenChange={(_, mdata) => setMenuOpen(mdata.open)}
              positioning={{
                target: menuVirtualTarget ?? undefined,
                position: 'below',
                align: 'start',
              }}
            >
              <MenuTrigger disableButtonEnhancement>
                <span style={{ display: 'none' }} />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {rowMenuOptions!.map((option) => {
                    const isDisabled =
                      typeof option.disabled === 'function'
                        ? menuItem
                          ? option.disabled(menuItem)
                          : true
                        : option.disabled;
                    return (
                      <MenuItem
                        key={option.key}
                        icon={option.icon}
                        disabled={isDisabled}
                        onClick={() => handleMenuOptionClick(option)}
                      >
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </MenuPopover>
            </Menu>
          </FluentProvider>
        )}
      </div>
    );
  }
);

export default PagedCardList;
